'use client';

import { useState, useEffect } from 'react';
import Logo from '@/UIComponents/Logo/Logo';
import { useTranslations } from 'use-intl';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { BRAND_COLORS } from '@/UIlibrary/globalTheme';
import { toast } from 'react-hot-toast';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import { useVerifyEmailMutation, VerifyEmailMutation } from '@/graphql/__generated__/accounts';
import logger from '@/logger';
import useAuthStore from '@/lib/auth';

const Verify: React.FC = () => {
	const { lang } = useParams();
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token');
	const { setToken } = useAuthStore();
	const navigate = useNavigate();

	const [error, setError] = useState<boolean>(false);
	const t = useTranslations('VerifyEmail');

	const accountsClient = useApolloClient(SCHEMAS.ACCOUNTS);

	const [verifyEmail, { loading: loadingVerifyEmail }] = useVerifyEmailMutation({
		client: accountsClient,
		onCompleted: (data: VerifyEmailMutation) => {
			const verifyData = data.verifyEmail;
			if (verifyData?.successful) {
				logger.debug('Navigating to login page after successful email verification');
				toast.success(t('VerifyEmailSuccess'), { duration: 15000 });
				navigate(`/${lang}/login`, { replace: true });
			}
			if (verifyData?.mutationErrors && verifyData.mutationErrors.length > 0) {
				verifyData.mutationErrors.forEach((error) => {
					if (error) {
						switch (error.__typename) {
							case 'EmailAlreadyVerified':
								toast.error(t('EmailAlreadyVerified'), { duration: 15000 });
								break;
							case 'ActionFailed':
							case 'InvalidAction':
								toast.error(t('ErrorVerifyingEmail'), { duration: 15000 });
								setError(true);
								break;
							default:
								setError(true);
								break;
						}
					}
				});
			}
		},
		onError: (error) => {
			logger.error('Error verifying email', error);
			toast.error(t('ErrorVerifyingEmail'), { duration: 15000 });
			navigate(`/${lang}/login`, { replace: true });
			setError(true);
		}
	});

	useEffect(() => {
		const handleConfirmation = async () => {
			if (token) {
				setToken(token);
				await verifyEmail();
			} else {
				setError(true);
			}
		};

		handleConfirmation();
	}, [setToken, token, verifyEmail]);

	if (loadingVerifyEmail) {
		return (
			<div className="flex justify-center items-center h-screen">
				<div className="loader"></div>
			</div>
		);
	}

	return (
		<div className="w-full h-screen flex flex-col justify-center items-center">
			<div className="flex flex-col w-11/12 md:w-full max-w-[450px] mx-auto bg-[#FFFFFF] py-12 px-11 rounded-[25px] gap-8">
				<div className="flex flex-row justify-between">
					<Logo src="/logo-square-white.jpg" width={110} height={110} />
				</div>
				<div className="flex flex-col gap-8 mt-6">
					<div className="flex flex-col gap-3">
						<Typography size="sm" type="display" weight="semibold">
							{t('VerifyEmail')}
						</Typography>
					</div>
					<div className="flex flex-col gap-2">
						<Typography size="md" color="subtitle">
							{error ? t('VerifyEmailErrorMessage') : t('VerifyEmailSuccess')}
						</Typography>
					</div>
					<div className="flex items-center justify-center p-2 gap-1 ">
						<Typography size="sm" color="subtitle">
							{t('GoBack')}{' '}
						</Typography>
						<Link to={`/${lang}/signin`}>
							<Typography size="sm" weight="semibold" color={BRAND_COLORS.brand700}>
								{t('Login')}
							</Typography>
						</Link>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Verify;
